/**
 * Auth Errors: IOTU Login Service (v2)
 * Error handling for authentication-related usage of IOTU Auth service.
 *
 * Login Error Types:
 * - FORBIDDEN, 403, { messages: ['Too many login attempts, please try again later'] }
 * - UNAUTHORIZED, 401, { messages: ['Wrong email or password'] }
 * - USER_NOT_FOUND, 404, { messages: ['Missing user data'] }
 * - UNEXPECTED_INTERNAL_ERROR, 500, { messages: ['Instance Not Found'] }
 *
 * Refresh Error Types:
 * - UNAUTHORIZED, 401, { messages: [verifiedRefreshPayload.error] }
 * - USER_NOT_FOUND, 404, { messages: ['Missing user data'] }
 * - UNAUTHORIZED, 401, { messages: ['Invalid JWT'] }
 * - UNEXPECTED_INTERNAL_ERROR, 500, { messages: ['Instance Not Found'] }
 * - UNAUTHORIZED, 401, { messages: ['Wrong email or password'] }
 */

import { toast } from '@Shelly/shelly-design-system/index';
import { format } from 'svelte-i18n';
import { get } from 'svelte/store';

export enum AuthErrorType {
	VALIDATION_ERRORS = 'VALIDATION_ERRORS', // Wrong email or password fields
	// Login/refresh errors
	FORBIDDEN = 'FORBIDDEN', // Too many login attempts, account temporarily locked
	UNAUTHORIZED = 'UNAUTHORIZED', // Invalid or expired refresh token; Wrong password
	USER_NOT_FOUND = 'USER_NOT_FOUND', // Invalid user (or broken user data)
	UNEXPECTED_INTERNAL_ERROR = 'UNEXPECTED_INTERNAL_ERROR', // Server error
}

export type AuthErroMessage = {
	heading: string;
	message: string;
};

/**
 * Session-invalidating errors that require force logout
 *  - These errors indicate that the user's session is invalid
 */
const sessionInvalidatingErrors = [
	AuthErrorType.FORBIDDEN, // Too many login attempts, account temporarily locked
	AuthErrorType.UNAUTHORIZED, // Invalid or expired refresh token; Wrong password
	AuthErrorType.USER_NOT_FOUND, // Invalid user (or broken user data)
	AuthErrorType.VALIDATION_ERRORS, // Invalid email or password
];

/**
 * Handle force logout alert
 *  Used in edge cases where the session is expired or invalid
 *  and the user needs to be logged out immediately.
 */
export const showForceLogoutAlert = () => {
	const _format = get(format);

	const title = _format('session_expired', {
		default: 'Session expired',
	});
	const text = _format('session_expired_description', {
		default: 'Please log in again.',
	});

	toast.open({
		title,
		text,
		type: 'danger',
	});
};

/**
 * Check if the error requires a force logout
 * @param response Auth request response object
 * @param response.error Error string from AuthErrorType enum
 * @returns {boolean} - True if the error requires a force logout, false otherwise
 */
export const shouldForceLogout = (
	response: Partial<{
		error: string;
		errors: any; //@todo: remove; Legacy handling for PHP API v1 errors
	}>,
) => {
	//Legacy handling for PHP API v1 errors
	//@ToDo: Remove after full migration to IOTU v2 login
	if (legacyIsSessionInvalidatingError(response?.errors)) {
		return true;
	}

	const error = response?.error as AuthErrorType;
	if (sessionInvalidatingErrors.includes(error)) {
		return true;
	}
	return false;
};

/**
 * Check if the error is a session invalidating error
 * @deprecated Remove after full migration to IOTU v2 login
 *
 * @param response Auth request response object
 * @param response.errors Error objects for the legacy PHP API v1
 * @returns {boolean} - True if the error is a session invalidating error, false otherwise
 */
const legacyIsSessionInvalidatingError = (errors: any): boolean => {
	if (!errors) return false;
	return !!(errors?.wrong_credentials || errors?.max_attempts);
};

/**
 * Handle authentication error messages
 * This function processes the error response from the authentication service
 * @param response
 * @param email  - Optional email for personalized error messages
 * @param response.error - Error string from AuthErrorType enum
 * @param response.status - HTTP status code of the response
 * @returns
 */
export const handleAuthErrorMessage = (
	response: { error: AuthErrorType; status?: number },
	email?: string,
): AuthErroMessage => {
	const error = response?.error;
	const _format = get(format);

	const alertProps = {
		heading: _format('alert_danger_error_heading', { default: 'Error' }),
		message: _format('err_msg_please_try_again', {
			default: 'Please try again!',
		}),
	};

	switch (error) {
		case AuthErrorType.VALIDATION_ERRORS:
		case AuthErrorType.UNAUTHORIZED:
		case AuthErrorType.USER_NOT_FOUND:
			alertProps.heading = _format('error_wrong_credentials', {
				default: `Wrong email or password!`,
			});
			alertProps.message = _format('no_account_desc', {
				default: "Sorry, we couldn't find an account with that email and password.",
			});

			break;
		case AuthErrorType.FORBIDDEN:
			alertProps.heading = _format('alert_danger_login_error_heading', {
				default: 'Login error',
			});
			alertProps.message = email
				? _format('locked_account_desc_with_email', {
						default:
							'Too many incorrect login attempts. Your account ({email}) is temporarily locked for one hour.',
						values: { email },
					})
				: _format('locked_account_desc', {
						default:
							'Too many incorrect login attempts. Your account is temporarily locked for one hour.',
					});
			break;
		case AuthErrorType.UNEXPECTED_INTERNAL_ERROR:
		default:
			//Transport errors
			if (response.status >= 500) {
				alertProps.message = _format('server_error_desc', {
					default: 'Server could not be reached. Please, try again.',
				});
			} else if (!response.status) {
				alertProps.heading = _format('error_no_connection', {
					default: 'Connection Error',
				});
				alertProps.message = _format('no_connection_desc', {
					default: 'Network connectivity problem. Please, try again.',
				});
			}
			break;
	}
	return alertProps;
};
